/**
 * Cmd+K Command Palette — Onda 16.4
 *
 * Saltar para qualquer rota em <1s.
 * Atalho: Cmd+K (mac) / Ctrl+K (win/linux).
 *
 * Não usa lib externa: implementação minimalista com fuzzy substring match.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, LayoutDashboard, GitCompare, Building2, ShieldAlert, FileSpreadsheet,
  TrendingUp, Wallet, AlertTriangle, ClipboardList, MapPin, Briefcase,
  GitBranch, Truck, Users, Calendar, BarChart3, FileText, Database, Activity,
  Settings, Award, Layers, Network,
} from "lucide-react";

type Cmd = {
  id: string;
  label: string;
  group: string;
  icon: typeof Search;
  href: string;
  keywords?: string[];
};

const COMMANDS: Cmd[] = [
  // Visão geral
  { id: "dashboard", label: "Cockpit", group: "Visão", icon: LayoutDashboard, href: "/app", keywords: ["home", "início"] },
  { id: "dashboard-v1", label: "Dashboard (clássico)", group: "Visão", icon: LayoutDashboard, href: "/app/dashboard-classic" },

  // Análise de risco
  { id: "losses", label: "Perdas e atraso", group: "Risco", icon: GitCompare, href: "/app/losses", keywords: ["npl", "atraso"] },
  { id: "aging", label: "Aging", group: "Risco", icon: Calendar, href: "/app/aging" },
  { id: "enquadramento", label: "Enquadramento", group: "Risco", icon: ShieldAlert, href: "/app/enquadramento" },
  { id: "ifrs9", label: "IFRS 9", group: "Risco", icon: BarChart3, href: "/app/ifrs9" },
  { id: "pdd", label: "PDD", group: "Risco", icon: AlertTriangle, href: "/app/pdd", keywords: ["provisão"] },
  { id: "vintage", label: "Safragem (vintage)", group: "Risco", icon: Layers, href: "/app/vintage" },
  { id: "payment-schedule", label: "Cronograma de pagamentos", group: "Risco", icon: Calendar, href: "/app/payment-schedule" },
  { id: "simulations", label: "Simulações", group: "Risco", icon: Activity, href: "/app/simulations" },
  { id: "regional-risk", label: "Risco regional", group: "Risco", icon: MapPin, href: "/app/regional-risk" },
  { id: "stress", label: "Stress test", group: "Risco", icon: Activity, href: "/app/stress-test", keywords: ["sensibilidade"] },

  // Carteira
  { id: "rate", label: "Taxa efetiva", group: "Carteira", icon: TrendingUp, href: "/app/rate" },
  { id: "cotas", label: "Cotas Sr/Mez/Jr", group: "Carteira", icon: Wallet, href: "/app/cotas" },
  { id: "agente-cobranca", label: "Agente cobrança / CNPJs devedores", group: "Carteira", icon: Building2, href: "/app/agente-cobranca", keywords: ["cnpj", "devedores", "empregador"] },
  { id: "fingerprint", label: "Fingerprint", group: "Carteira", icon: Network, href: "/app/fingerprint", keywords: ["risco identitário"] },
  { id: "subordinacao", label: "Subordinação & liquidez", group: "Carteira", icon: Layers, href: "/app/subordinacao-liquidez" },
  { id: "distribuicoes", label: "Distribuições", group: "Carteira", icon: Wallet, href: "/app/distribuicoes" },

  // Operacional
  { id: "loan-tape", label: "Loan tape", group: "Operacional", icon: FileSpreadsheet, href: "/app/loan-tape" },
  { id: "originadoras", label: "Originadoras (ranking)", group: "Operacional", icon: Award, href: "/app/originadoras", keywords: ["score", "tier"] },
  { id: "concessoes", label: "Concessões (pipeline)", group: "Operacional", icon: GitBranch, href: "/app/concessoes", keywords: ["kanban", "cessão"] },
  { id: "roll-rate", label: "Roll rate & cure", group: "Operacional", icon: GitCompare, href: "/app/roll-rate" },
  { id: "recompra", label: "Recompra", group: "Operacional", icon: Truck, href: "/app/recompra" },
  { id: "conciliation", label: "Conciliação", group: "Operacional", icon: FileSpreadsheet, href: "/app/conciliation" },
  { id: "recon-matching", label: "Conciliação aproximada", group: "Operacional", icon: FileSpreadsheet, href: "/app/recon-matching" },
  { id: "dataprev", label: "Dataprev / repasses", group: "Operacional", icon: Database, href: "/app/dataprev" },
  { id: "imports", label: "Importar CSV", group: "Operacional", icon: FileText, href: "/app/imports" },
  { id: "reports", label: "Relatórios", group: "Operacional", icon: FileText, href: "/app/reports" },

  // Compliance
  { id: "kyc", label: "KYC / PLD trail", group: "Compliance", icon: ClipboardList, href: "/app/kyc", keywords: ["pep", "sanção", "due diligence"] },
  { id: "peer-benchmark", label: "Peer benchmark", group: "Compliance", icon: BarChart3, href: "/app/peer-benchmark" },
  { id: "audit-internal", label: "Auditoria interna", group: "Compliance", icon: FileText, href: "/app/audit-internal" },

  // Admin
  { id: "admin-users", label: "Usuários", group: "Admin", icon: Users, href: "/app/admin/users" },
  { id: "admin-companies", label: "Empresas", group: "Admin", icon: Building2, href: "/app/admin/companies" },
  { id: "admin-audit", label: "Auditoria sistema", group: "Admin", icon: FileText, href: "/app/admin/audit" },
  { id: "admin-fidc-tranches", label: "FIDC tranches", group: "Admin", icon: Layers, href: "/app/admin/fidc-tranches" },
  { id: "admin-automation", label: "Automation", group: "Admin", icon: Settings, href: "/app/admin/automation" },
  { id: "admin-system-health", label: "System health", group: "Admin", icon: Activity, href: "/app/admin/system-health" },
  { id: "admin-cdi-forecast", label: "CDI forecast", group: "Admin", icon: TrendingUp, href: "/app/admin/interest-rate-forecast" },
  { id: "admin-trajectory-quality", label: "Qualidade trajetória (admin)", group: "Admin", icon: ShieldAlert, href: "/app/admin/trajectory-quality", keywords: ["djen", "datajud", "cobertura"] },
];

function fuzzyMatch(haystack: string, query: string): boolean {
  if (!query) return true;
  const q = query.toLowerCase().trim();
  const h = haystack.toLowerCase();
  // substring direto OU sequência de chars na ordem
  if (h.includes(q)) return true;
  let qi = 0;
  for (let i = 0; i < h.length && qi < q.length; i++) {
    if (h[i] === q[qi]) qi++;
  }
  return qi === q.length;
}

export function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [activeIdx, setActiveIdx] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((v) => !v);
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 0);
    }
  }, [open]);

  const filtered = useMemo(() => {
    return COMMANDS.filter((c) => {
      const hay = [c.label, c.group, ...(c.keywords ?? [])].join(" ");
      return fuzzyMatch(hay, query);
    });
  }, [query]);

  function go(href: string) {
    setOpen(false);
    navigate(href);
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const cmd = filtered[activeIdx];
      if (cmd) go(cmd.href);
    }
  }

  if (!open) return null;

  // Group by category preserving order
  const byGroup = new Map<string, Cmd[]>();
  for (const c of filtered) {
    if (!byGroup.has(c.group)) byGroup.set(c.group, []);
    byGroup.get(c.group)!.push(c);
  }

  let runningIdx = -1;

  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center pt-[15vh] px-4" onClick={() => setOpen(false)}>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-xl bg-card rounded-lg shadow-2xl border border-border overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 px-4 py-3 border-b border-border">
          <Search className="h-4 w-4 text-muted-foreground" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={onKeyDown}
            placeholder="Saltar para… (digite uma tela)"
            className="flex-1 bg-transparent outline-none text-sm placeholder:text-muted-foreground"
          />
          <kbd className="text-[10px] text-muted-foreground border border-border rounded px-1.5 py-0.5">ESC</kbd>
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-4 py-10 text-center text-sm text-muted-foreground">
              Nada encontrado pra <span className="font-mono">"{query}"</span>.
            </div>
          ) : (
            Array.from(byGroup.entries()).map(([group, cmds]) => (
              <div key={group}>
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">
                  {group}
                </div>
                {cmds.map((c) => {
                  runningIdx++;
                  const isActive = runningIdx === activeIdx;
                  const Icon = c.icon;
                  return (
                    <button
                      key={c.id}
                      onClick={() => go(c.href)}
                      onMouseEnter={() => setActiveIdx(runningIdx)}
                      className={`w-full flex items-center gap-3 px-4 py-2 text-left text-sm transition-colors ${
                        isActive ? "bg-primary-medium/10 text-primary-dark" : "hover:bg-muted/60"
                      }`}
                    >
                      <Icon className={`h-4 w-4 shrink-0 ${isActive ? "text-primary-medium" : "text-muted-foreground"}`} />
                      <span className="flex-1">{c.label}</span>
                      <span className="text-[10px] text-muted-foreground font-mono">{c.href}</span>
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>
        <div className="px-4 py-2 border-t border-border bg-muted/40 text-[10px] text-muted-foreground flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span><kbd className="border border-border rounded px-1">↑↓</kbd> navegar</span>
            <span><kbd className="border border-border rounded px-1">↵</kbd> abrir</span>
          </div>
          <span>{filtered.length} de {COMMANDS.length}</span>
        </div>
      </div>
    </div>
  );
}
